import React, { Component } from 'react';
import { withTranslation, WithTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import './ForgotPasswordPage.css';

// Import images
import { ArrowLeftBlack } from '../../../assets/icons';

// Import Redux actions
import ForgotPasswordActions from '../../../redux/ForgotPasswordRedux';
import { ForgotPasswordSelectors } from '../../../redux/ForgotPasswordRedux';

// Back Arrow Icon Component
const BackArrowIcon: React.FC = () => (
  <ArrowLeftBlack />
);

interface ForgotPasswordPageState {
  email: string;
  emailError: string | null;
  props?: ForgotPasswordPageProps;
}

interface ForgotPasswordPageProps extends WithTranslation {
  // Redux props
  forgotPasswordFetching?: boolean;
  forgotPasswordSuccess?: boolean;
  forgotPasswordError?: boolean;
  forgotPassword?: (email: string) => void;
  // Navigation props
  navigate?: (path: string) => void;
  goBack?: () => void;
}

class ForgotPasswordPage extends Component<ForgotPasswordPageProps, ForgotPasswordPageState> {
  constructor(props: ForgotPasswordPageProps) {
    super(props);
    this.state = {
      email: '',
      emailError: null,
    };
  }

  // Handle error display
  componentDidUpdate(prevProps: ForgotPasswordPageProps) {
    if (this.props.forgotPasswordError && this.props.forgotPasswordError !== prevProps.forgotPasswordError) {
      this.showErrorAlert(this.props.t('forgot-password-screen:alert.fail-message'));
    }
  }

  // Implement back navigation
  back() {
    if (this.props.goBack) {
      this.props.goBack();
    } else {
      // Fallback to browser back
      window.history.back();
    }
  }

  // Implement forgot password functionality
  _onSubmitPress() {
    if (this.props.forgotPassword) {
      this.props.forgotPassword(this.state.email);
    }
  }

  // Custom error alert
  showErrorAlert = (message: string) => {
    // Create a custom alert element
    const alertDiv = document.createElement('div');
    alertDiv.className = 'custom-alert';
    alertDiv.innerHTML = `
      <div class="alert-content">
        <div class="alert-message">${message}</div>
        <button class="alert-button" onclick="this.parentElement.parentElement.remove()">OK</button>
      </div>
    `;
    
    // Add styles
    alertDiv.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0, 0, 0, 0.5);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 1000;
    `;
    
    // Add to body
    document.body.appendChild(alertDiv);
    
    // Auto remove after 5 seconds
    setTimeout(() => {
      if (alertDiv.parentElement) {
        alertDiv.remove();
      }
    }, 5000);
  }

  // Custom success alert with navigation
  showSuccessAlert = (message: string) => {
    // Create a custom alert element
    const alertDiv = document.createElement('div');
    alertDiv.className = 'custom-alert';
    alertDiv.innerHTML = `
      <div class="alert-content">
        <div class="alert-message">${message}</div>
        <button class="alert-button" onclick="this.parentElement.parentElement.remove(); window.location.href='/login'">OK</button>
      </div>
    `;
    
    // Add styles
    alertDiv.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0, 0, 0, 0.5);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 1000;
    `;
    
    // Add to body
    document.body.appendChild(alertDiv);
    
    // Auto remove after 5 seconds
    setTimeout(() => {
      if (alertDiv.parentElement) {
        alertDiv.remove();
      }
    }, 5000);
  }

  // Implement getDerivedStateFromProps for Redux state handling
  static getDerivedStateFromProps(nextProps: ForgotPasswordPageProps, prevState: ForgotPasswordPageState) {
    if (prevState.props) {
      if (!nextProps.forgotPasswordFetching) {
        if (nextProps.forgotPasswordSuccess && nextProps.forgotPasswordSuccess !== prevState.props.forgotPasswordSuccess) {
          // Show success message and navigate to login
          setTimeout(() => {
            if (nextProps.navigate) {
              nextProps.navigate('/login');
            } else {
              window.location.href = '/login';
            }
          }, 2000);
          
          return {
            email: '',
          };
        }

        if (nextProps.forgotPasswordError && nextProps.forgotPasswordError !== prevState.props.forgotPasswordError) {
          // Error handling is done in componentDidUpdate
          console.error('Forgot password error:', nextProps.forgotPasswordError);
        }
      }
    }

    return {
      props: nextProps,
    };
  }

  handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({ email: e.target.value });
  };

  // Form validation
  validateEmail = (email: string): string | null => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email) {
      return 'Email is required';
    }
    if (!emailRegex.test(email)) {
      return 'Please enter a valid email address';
    }
    return null;
  };

  handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate email field
    const emailError = this.validateEmail(this.state.email);
    
    this.setState({
      emailError,
    });
    
    // If any errors, show alert and return
    if (emailError) {
      this.showErrorAlert('Please fill in your email address');
      return;
    }
    
    // If no errors, proceed with forgot password request
    this._onSubmitPress();
    
    // Show success message immediately (as per reference implementation)
    this.showSuccessAlert(this.props.t('forgot-password-screen:alert.message'));
  };

  handleBackClick = () => {
    this.back();
  };

  render() {
    const { email, emailError } = this.state;
    const { t, forgotPasswordFetching, forgotPasswordSuccess, forgotPasswordError } = this.props;

    return (
      <div className="forgotpassword-page">
        {/* Header */}
        <header className="forgotpassword-header">
          <div className="header-left">
            <button 
              className="back-button"
              onClick={this.handleBackClick}
              type="button"
            >
              <BackArrowIcon />
            </button>
            <h1 className="header-title">
                {t('forgot-password-screen:title')}
            </h1>
          </div>
          <div className="header-right"></div>
        </header>

        {/* Main Content */}
        <div className="forgotpassword-body">
          <div className="forgotpassword-container">
            {/* Message Section */}
            <div className="message-container">
              <p className="message-text">
                {t('forgot-password-screen:content')}
              </p>
            </div>

            {/* Form Section */}
            <form className="form-container" onSubmit={this.handleSubmit}>
              {/* Email Input */}
              <div className="form-group">
                <label className="form-label uppercase">
                  {t('forgot-password-screen:form.label.email')}
                </label>
                <div className="input-item">
                  <input
                    type="email"
                    className={`form-input ${emailError ? 'error' : ''}`}
                    placeholder={t('forgot-password-screen:form.placeholder.email')}
                    value={email}
                    onChange={this.handleEmailChange}
                    autoCapitalize="none"
                    required
                  />
                  {emailError && <div className="error-message">{emailError}</div>}
                </div>
              </div>
            </form>

            {/* Submit Button */}
            <div className="button-container">
              <button 
                type="button" 
                className="submit-button comman-btn w-100" 
                onClick={this.handleSubmit}
                disabled={forgotPasswordFetching}
              >
                <span className="button-text uppercase">
                  {forgotPasswordFetching ? 'Submitting...' : t('forgot-password-screen:button.submit')}
                </span>
              </button>
            </div>

          </div>
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state: any) => ({
  forgotPasswordFetching: state.forgotPassword.fetching,
  forgotPasswordSuccess: state.forgotPassword.success,
  forgotPasswordError: state.forgotPassword.error,
});

const mapDispatchToProps = (dispatch: any) => ({
  forgotPassword: (email: string) => dispatch(ForgotPasswordActions.forgotPasswordRequest(email)),
});

const ForgotPasswordPageWithNavigation: React.FC<WithTranslation> = (props) => {
  const navigate = useNavigate();

  const handleNavigate = (path: string) => {
    navigate(path);
  };

  const handleGoBack = () => {
    navigate(-1);
  };

  return (
    <ForgotPasswordPage
      {...props}
      navigate={handleNavigate}
      goBack={handleGoBack}
    />
  );
};

export default withTranslation()(connect(mapStateToProps, mapDispatchToProps)(ForgotPasswordPageWithNavigation));
